// Partner / carrier display. Renders each partner as its official logo when a
// trademark asset has been sourced (files in /public/logos), and falls back to a
// tasteful brand-colored text wordmark for any partner whose logo we do not yet
// have. Arranged in a responsive grid. Owned by Agent D.
//
// Partner names come from the company profile (content.md). Logo asset files are
// the trademarks of their respective owners, sourced from public brand pages /
// Wikimedia Commons / reputable logo CDNs; these marks identify cooperation only.
import { motion } from "framer-motion";

import { fadeUp, stagger, revealViewport } from "../motion/variants";

export interface Partner {
  /** Latin / primary display name shown in the wordmark + img alt text. */
  name: string;
  /** Optional Chinese display name shown when locale is zh. */
  zh?: string;
  /** Recognizable brand color for the wordmark text (used when no logo). */
  color: string;
  /**
   * Path (under /public) to an official logo asset. When set, the logo image is
   * rendered instead of the text wordmark. Omit to fall back to the wordmark.
   */
  logo?: string;
}

// --- Strategic shipping carriers (from content.md 公司背景) ---
export const CARRIERS: Partner[] = [
  { name: "COSCO SHIPPING", zh: "中远海运", color: "#0a4595", logo: "/logos/cosco.svg" },
  { name: "MSC", color: "#1b2f6b", logo: "/logos/msc.svg" },
  { name: "MAERSK", color: "#0099cc", logo: "/logos/maersk.svg" },
  { name: "EVERGREEN", zh: "长荣海运", color: "#0a7d4a", logo: "/logos/evergreen.svg" },
  { name: "APL", color: "#003f87", logo: "/logos/apl.svg" },
  { name: "OOCL", color: "#e2231a", logo: "/logos/oocl.svg" },
  { name: "RCL", color: "#c8102e", logo: "/logos/rcl.png" },
  { name: "SITC", zh: "海丰国际", color: "#005baa", logo: "/logos/sitc.svg" },
  // No clean trademark asset sourced yet — render as wordmark.
  { name: "DINGHENG", zh: "鼎衡", color: "#1f5fa6" },
  { name: "SHENGHANG", zh: "盛航海运", color: "#13738a" },
  { name: "GUOHE", zh: "国和海运", color: "#1a4a7a" },
];

// --- Industry partners (terminals, materials, conglomerates) ---
export const INDUSTRY: Partner[] = [
  { name: "ZOOMLION", zh: "中联重科", color: "#e60012", logo: "/logos/zoomlion.svg" },
  // No clean trademark asset sourced yet — render as wordmark.
  { name: "CNGR", zh: "中伟新材料", color: "#0a64a6" },
];

// --- In discussion (content.md: 德迅 / 中国外运, e-commerce air freight) ---
export const IN_DISCUSSION: Partner[] = [
  { name: "Kuehne+Nagel", zh: "德迅", color: "#005a9b", logo: "/logos/kuehne-nagel.svg" },
  // No clean trademark asset sourced yet — render as wordmark.
  { name: "Sinotrans", zh: "中国外运", color: "#c8102e" },
];

interface PartnerStripProps {
  partners: Partner[];
  lang: string;
  /** Render muted (used for the "in discussion" row). */
  muted?: boolean;
  columns?: 2 | 3 | 4 | 5;
}

const colClass: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
};

export default function PartnerStrip({
  partners,
  lang,
  muted = false,
  columns = 4,
}: PartnerStripProps) {
  return (
    <motion.ul
      variants={stagger}
      initial="hidden"
      whileInView="visible"
      viewport={revealViewport}
      className={`grid gap-4 ${colClass[columns]}`}
    >
      {partners.map((p) => {
        const showZh = lang === "zh" && p.zh;
        const altText = p.zh ? `${p.name} · ${p.zh}` : p.name;
        return (
          <motion.li key={p.name} variants={fadeUp}>
            <div
              className={`flex h-24 flex-col items-center justify-center rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-white px-4 text-center transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--brand-tint-2)] hover:shadow-[var(--shadow-card)] ${
                muted ? "opacity-80" : ""
              }`}
              title={altText}
            >
              {p.logo ? (
                // Official trademark logo. Constrain height, let width scale, and
                // apply a subtle grayscale treatment that lifts to full color on
                // hover so the strip reads as a cohesive band rather than a riot
                // of competing brand colors.
                <img
                  src={p.logo}
                  alt={altText}
                  loading="lazy"
                  decoding="async"
                  className="max-h-12 w-auto max-w-full object-contain opacity-85 transition-all duration-300 [filter:grayscale(1)] hover:opacity-100 hover:[filter:grayscale(0)]"
                />
              ) : (
                <>
                  <span
                    className="font-sans text-[var(--text-lg)] font-bold leading-tight tracking-tight"
                    style={{ color: p.color }}
                  >
                    {showZh ? p.zh : p.name}
                  </span>
                  {showZh ? (
                    <span className="mt-1 text-[var(--text-xs)] font-medium uppercase tracking-wide text-[var(--brand-slate-text)]">
                      {p.name}
                    </span>
                  ) : (
                    p.zh && (
                      <span className="font-zh mt-1 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                        {p.zh}
                      </span>
                    )
                  )}
                </>
              )}
            </div>
          </motion.li>
        );
      })}
    </motion.ul>
  );
}
